/**
 * Werknemers Overzicht Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { werknemersService, Werknemer, WerknemerFilters } from '../../services/werknemers.service';
import { authService } from '../../services/auth.service';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import '../relations/Relations.css';
import './WerknemersList.css';

const WerknemersList = () => {
  const [werknemers, setWerknemers] = useState<Werknemer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActief, setFilterActief] = useState<boolean | undefined>(true);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const navigate = useNavigate();

  // Sticky header state
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  const pageSize = 50;

  const loadWerknemers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: WerknemerFilters = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        actief: filterActief,
        zoek: searchTerm || undefined,
        sorteer: 'naam',
        richting: 'asc',
      };

      const response = await werknemersService.getAll(filters);
      setWerknemers(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van werknemers');
      console.error('Error loading werknemers:', err);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, filterActief]);

  useEffect(() => {
    loadWerknemers();
  }, [loadWerknemers]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        console.log('Current user loaded:', user);
        console.log('is_superuser:', user?.is_superuser);
        console.log('permissions:', user?.permissions);
        setCurrentUser(user);
      } catch (err) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);

  // Sticky header scroll listener
  useEffect(() => {
    if (!werknemers.length) return; // Wait for werknemers to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [werknemers]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      setDeleting(true);
      await werknemersService.delete(id);
      setDeleteId(null);
      loadWerknemers();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen van werknemer');
    } finally {
      setDeleting(false);
    }
  };

  const getVolledigeNaam = (werknemer: Werknemer) => {
    const parts = [werknemer.voornaam];
    if (werknemer.tussenvoegsel) parts.push(werknemer.tussenvoegsel);
    parts.push(werknemer.achternaam);
    return parts.join(' ');
  };

  const canEditWerknemer = (werknemer: Werknemer) => {
    if (!currentUser) return false;
    // Superuser kan alles
    if (currentUser.is_superuser === true) return true;
    // Heeft gebruikers_beheren permissie
    if (currentUser.permissions?.includes('gebruikers_beheren')) return true;
    // Kan eigen gegevens bewerken (als werknemer ingelogd is)
    if (currentUser.id === werknemer.id) return true;
    return false;
  };

  const canDeleteWerknemer = () => {
    if (!currentUser) return false;
    // Superuser kan alles
    if (currentUser.is_superuser === true) return true;
    // Of heeft gebruikers_beheren permissie
    return currentUser.permissions?.includes('gebruikers_beheren') === true;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="relations-list">
      <div className="page-header">
        <h1>Werknemers</h1>
        {(currentUser?.is_superuser || currentUser?.permissions?.includes('gebruikers_beheren')) && (
          <button
            className="btn-lg btn-primary"
            onClick={() => navigate('/werknemers/new')}
          >
            + Nieuwe Werknemer
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="search-bar">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <input
            type="text"
            placeholder="Zoek op naam, email, functie of personeelsnummer..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
          <span className="search-results">
            {total} {total === 1 ? 'werknemer' : 'werknemers'}
          </span>
        </div>

        <select
          value={filterActief === undefined ? 'all' : filterActief ? 'actief' : 'inactief'}
          onChange={(e) => {
            const val = e.target.value;
            setFilterActief(val === 'all' ? undefined : val === 'actief');
            setPage(1);
          }}
          style={{
            padding: '0.625rem 1rem',
            fontSize: '0.9375rem',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '150px',
          }}
        >
          <option value="all">Alle</option>
          <option value="actief">Actief</option>
          <option value="inactief">Inactief</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Werknemers laden...
        </div>
      ) : werknemers.length === 0 ? (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '1rem' }}>Geen werknemers gevonden</h3>
          <p style={{ color: '#64748b' }}>
            {searchTerm
              ? 'Probeer een andere zoekterm'
              : 'Voeg je eerste werknemer toe om te beginnen'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="werknemers-table-container">
            {/* Vaste header rij (altijd zichtbaar) */}
            {werknemers.length > 0 && (
              <div ref={tableHeaderRef} style={{
                display: 'flex',
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                padding: '0.75rem 1rem',
                marginTop: '1rem',
                borderRadius: '8px 8px 0 0',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#495057'
              }}>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Naam</div>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Email</div>
                <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Telefoon</div>
                <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Status</div>
                <div style={{ flex: '1.3', minWidth: 0 }}>Acties</div>
              </div>
            )}

            {/* Sticky header overlay (duplicaat) */}
            {isHeaderSticky && (
              <div style={{
                position: 'fixed',
                top: '185px',
                left: `${tableLeft}px`,
                width: `${tableWidth}px`,
                zIndex: 1000,
                display: 'flex',
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                borderRadius: '8px 8px 0 0',
                padding: '0.75rem 1rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#495057'
              }}>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Naam</div>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Email</div>
                <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Telefoon</div>
                <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Status</div>
                <div style={{ flex: '1.3', minWidth: 0 }}>Acties</div>
              </div>
            )}

            {/* Werknemers lijst */}
            {werknemers.length > 0 && (
              <div style={{
                background: 'white',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                {werknemers.map((werknemer) => (
                  <div key={werknemer.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ flex: '1.5', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getVolledigeNaam(werknemer)}
                    </div>
                    <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {werknemer.email ? (
                        <a href={`mailto:${werknemer.email}`} style={{ color: '#4A9EFF' }}>
                          {werknemer.email}
                        </a>
                      ) : (
                        '-'
                      )}
                    </div>
                    <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {werknemer.mobiel || werknemer.telefoon || '-'}
                    </div>
                    <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center' }}>
                      <span
                        className={`status-badge ${werknemer.actief ? 'status-ok' : 'status-kritiek'}`}
                      >
                        {werknemer.actief ? 'Actief' : 'Inactief'}
                      </span>
                    </div>
                    <div className="actions" style={{ flex: '1.3', minWidth: 0 }}>
                      {canEditWerknemer(werknemer) && (
                        <button
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/werknemers/${werknemer.id}`)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Bewerken
                        </button>
                      )}
                      {canDeleteWerknemer() && (
                        <button
                          className="btn-small btn-danger"
                          onClick={() => setDeleteId(werknemer.id)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Verwijder
                        </button>
                      )}
                      {!canEditWerknemer(werknemer) && !canDeleteWerknemer() && (
                        <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>-</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Cards */}
          <div className="mobile-werknemers-cards">
            {werknemers.map((werknemer) => (
              <div key={werknemer.id} className="mobile-werknemer-card">
                <div className="mobile-werknemer-header">
                  {getVolledigeNaam(werknemer)}
                </div>
                <div className="mobile-werknemer-content">
                  <div className="mobile-werknemer-row">
                    <span className="mobile-werknemer-label">Email:</span>
                    <span className="mobile-werknemer-value">
                      {werknemer.email ? (
                        <a href={`mailto:${werknemer.email}`} style={{ color: '#4A9EFF' }}>
                          {werknemer.email}
                        </a>
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                  <div className="mobile-werknemer-row">
                    <span className="mobile-werknemer-label">Telefoon:</span>
                    <span className="mobile-werknemer-value">
                      {werknemer.mobiel || werknemer.telefoon || '-'}
                    </span>
                  </div>
                  <div className="mobile-werknemer-row">
                    <span className="mobile-werknemer-label">Functie:</span>
                    <span className="mobile-werknemer-value">
                      {werknemer.functie || '-'}
                    </span>
                  </div>
                  <div className="mobile-werknemer-row">
                    <span className="mobile-werknemer-label">Status:</span>
                    <span className="mobile-werknemer-value">
                      <span className={`mobile-status-badge ${werknemer.actief ? 'mobile-status-actief' : 'mobile-status-inactief'}`}>
                        {werknemer.actief ? 'Actief' : 'Inactief'}
                      </span>
                    </span>
                  </div>
                  {(canEditWerknemer(werknemer) || canDeleteWerknemer()) && (
                    <div className="mobile-werknemer-actions">
                      {canEditWerknemer(werknemer) && (
                        <button
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/werknemers/${werknemer.id}`)}
                          style={{ color: 'white !important' }}
                        >
                          Bewerken
                        </button>
                      )}
                      {canDeleteWerknemer() && (
                        <button
                          className="btn-small btn-danger"
                          onClick={() => setDeleteId(werknemer.id)}
                          style={{ color: 'white !important' }}
                        >
                          Verwijderen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '1rem',
              marginTop: '1.5rem'
            }}>
              <button
                className="btn-small btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Vorige
              </button>
              <span>Pagina {page} van {totalPages}</span>
              <button
                className="btn-small btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Volgende
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        title="Werknemer Verwijderen"
        message={(() => {
          const werknemer = werknemers.find(w => w.id === deleteId);
          if (!werknemer) return 'Weet je zeker dat je deze werknemer wilt verwijderen?';
          return `Weet je zeker dat je "${getVolledigeNaam(werknemer)}" wilt verwijderen?`;
        })()}
        confirmText={deleting ? 'Verwijderen...' : 'Verwijderen'}
        cancelText="Annuleren"
        onConfirm={() => deleteId && handleDelete(deleteId)}
        onCancel={() => setDeleteId(null)}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default WerknemersList;
